import { useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { IonApp, setupIonicReact } from '@ionic/react';
import { YbugProvider } from 'ybug-react';
import { queryClient } from './app/queryClient';
import { router } from './app/router';
import { InitAuth } from './services/authService';
import { InitWs, setWsQueryClient } from './services/wsService';
import { InitPush } from './services/pushService';
import { InitDevice } from './services/deviceService';
import { initPosthog } from './services/posthogService';
import { InitYbug } from './services/ybugInit';
import { isIOS, ybugSettings } from './services/ybugSettings';
import { logInfo } from './services/loggerService';

// WebSocket events invalidate query keys (events → data refresh).
setWsQueryClient(queryClient);

// PostHog before any auth/identify code runs. No-op when VITE_POSTHOG_KEY is unset.
initPosthog('admin');

// Ionic is kept as a platform (FE plan §3.9): `IonApp` is mounted once so
// Ionic overlays work anywhere, but desktop screens don't use Ionic
// components and routing is TanStack Router, not IonReactRouter.
setupIonicReact({ mode: 'md' });

// Ybug is on only when the env names a project AND enables it; an empty ID
// disables it regardless. "Contact support" in the error modal needs it.
const YBUG_ID = import.meta.env.VITE_YBUG_ID || '';
const ybugEnabled = import.meta.env.VITE_SHOW_YBUG === 'true' && YBUG_ID !== '';

declare global {
  interface Window {
    __hpBootDone?: () => void;
  }
}

if (isIOS && typeof document !== 'undefined') {
  document.documentElement.classList.add('hp-ios');
}

export default function App() {
  useEffect(() => {
    // Clear the index.html boot watchdog: React mounted.
    window.__hpBootDone?.();
    logInfo(`App loaded - Version: ${import.meta.env.VITE_VERSION}`);
  }, []);

  // Small screens get the shell's phone layout; screens not turned on for
  // phones show a notice there (app/shell/ScreenSizeGate).
  const app = (
    <>
      <InitDevice />
      <InitAuth />
      <InitWs />
      <InitPush />
      <IonApp>
        <RouterProvider router={router} />
      </IonApp>
    </>
  );

  return (
    <QueryClientProvider client={queryClient}>
      {ybugEnabled ? (
        <YbugProvider ybugId={YBUG_ID} settings={ybugSettings}>
          <InitYbug />
          {app}
        </YbugProvider>
      ) : (
        app
      )}
    </QueryClientProvider>
  );
}
